// ---------------------------------------------------------------------------
// Dynamic ObjectDefs — runtime-registered content (imported card sets).
//
// The static def registry (shared/defs.ts) covers built-in content only.
// Imports (e.g. MTG decks via Scryfall) create defs at runtime; those defs
// must be known to BOTH sides, so they travel over the wire:
//   • server → client: in the "welcome" message (late joiners) and via
//     {t:"defs"} broadcasts when an import adds new ones,
//   • and they persist inside the room save file so decks survive restarts.
//
// This module is isomorphic: the server registers defs after resolving an
// import; the client applies def arrays received from the server. Both go
// through `registerDynamicDefs`, which validates shapes and refuses to
// clobber built-in defs.
// ---------------------------------------------------------------------------

import { DEFS, registerDef, type FaceSpec, type ObjectDef, type ObjKind } from "./defs";

// ------------------------------------------------- wire-protocol extensions
// These are the canonical shapes for the protocol.ts unions (spliced in by
// the integration edits — see src/server/import/INTEGRATION.md).

/** Which importer resolves the list. Omitted = "mtg" (pre-selector clients). */
export type ImportGame = "mtg" | "pokemon" | "lorcana";

/** C→S: import a deck at a table point. `list` is the pasted deck list. */
export type ImportDeckIntent = {
  t: "importDeck";
  list: string;
  url?: string;
  game?: ImportGame;
  x: number;
  z: number;
};

/** S→C: dynamic defs were added; apply via `registerDynamicDefs`. */
export type DefsServerMsg = { t: "defs"; defs: ObjectDef[] };

// ------------------------------------------------------------------ registry

/** Ids of defs registered dynamically in this process. */
const dynamicIds = new Set<string>();

const KINDS: readonly ObjKind[] = ["card", "stack", "token", "board", "zone", "die"];

function validFace(f: FaceSpec | undefined): boolean {
  if (f === undefined) return true;
  if (typeof f !== "object" || f === null || typeof (f as { type?: unknown }).type !== "string") return false;
  if (f.type === "image") {
    // Only fetchable image sources — never javascript:/file: etc.
    // A single leading slash is a same-origin asset (e.g. /card_backs/…).
    return typeof f.url === "string" && /^(https?:\/\/|data:image\/|\/(?!\/))/.test(f.url);
  }
  return true;
}

/** Structural sanity check for defs received over the wire / from disk. */
export function isValidDynamicDef(d: unknown): d is ObjectDef {
  if (typeof d !== "object" || d === null) return false;
  const o = d as Partial<ObjectDef>;
  return (
    typeof o.id === "string" && o.id.length > 0 && o.id.length <= 128 &&
    typeof o.name === "string" &&
    KINDS.includes(o.kind as ObjKind) &&
    Array.isArray(o.size) && o.size.length === 3 && o.size.every((n) => typeof n === "number" && isFinite(n) && n > 0) &&
    validFace(o.front) && validFace(o.back)
  );
}

/**
 * Register a batch of dynamic defs into the shared DEFS registry.
 * Invalid defs are dropped; built-in (non-dynamic) ids are never overwritten.
 * Returns the defs actually applied. Idempotent.
 */
export function registerDynamicDefs(defs: ObjectDef[] | undefined | null): ObjectDef[] {
  const applied: ObjectDef[] = [];
  for (const def of defs ?? []) {
    if (!isValidDynamicDef(def)) continue;
    if (DEFS.has(def.id) && !dynamicIds.has(def.id)) continue; // built-in — keep
    dynamicIds.add(def.id);
    registerDef(def);
    applied.push(def);
  }
  return applied;
}

/** All dynamic defs known to this process (serializable as plain JSON). */
export function getDynamicDefs(): ObjectDef[] {
  const out: ObjectDef[] = [];
  for (const id of dynamicIds) {
    const d = DEFS.get(id);
    if (d) out.push(d);
  }
  return out;
}

export function isDynamicDef(id: string): boolean {
  return dynamicIds.has(id);
}

/** Remove all dynamic defs (tests / room teardown). */
export function clearDynamicDefs(): void {
  for (const id of dynamicIds) DEFS.delete(id);
  dynamicIds.clear();
}
